import { renderProviderName } from '../../../helpers/helpers';
import { playbookFoodhubConnect } from '../../../assets';
import {
  DEFAULT_CONNECTION_TIMING,
  TIMING_TABLE_PROVIDERS,
  providerConnectionTiming,
} from '../providerTiming';

/**
 * The integration playbook, as copy. Everything the team reads lives here so
 * the wording can change without touching the components.
 *
 * It is a reference, not a wizard: nothing is ticked off and nothing is
 * remembered. Someone opens it to check a rule or a timing, reads the section
 * they need and closes it again.
 */

export type PlaybookTabId = 'pos' | 'marketplaces';

export type PlaybookLink =
  /** Switches the Manage Providers tab and closes the playbook. */
  | { kind: 'tab'; tab: 'pos' | 'marketplace'; label: string }
  /** Opens an in-app page in a new tab so the playbook stays put. */
  | { kind: 'url'; href: string; label: string }
  /** Working Hours needs the location/brand picker seeded first. */
  | { kind: 'workingHours'; label: string };

export type PlaybookTiming = {
  provider: string;
  timing: string;
  /** Why it takes that long, where the reason is worth knowing. */
  note?: string;
};

export type PlaybookBlock =
  | { kind: 'text'; text: string }
  | { kind: 'bullets'; items: string[] }
  /** A flow that has to happen in this order, not a checklist. */
  | { kind: 'steps'; items: string[] }
  | { kind: 'callout'; tone: 'danger' | 'info'; text: string }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'timings'; rows: PlaybookTiming[] }
  | { kind: 'links'; links: PlaybookLink[] };

export type PlaybookSection = {
  id: string;
  title: string;
  blocks: PlaybookBlock[];
};

/**
 * The written walkthrough for each POS, hosted on the vGrubs CDN rather than
 * bundled: they are a few MB each and change on their own schedule.
 */
const POS_GUIDE_PDFS = {
  clover:
    'https://vgrubs-prod.nyc3.cdn.digitaloceanspaces.com/guides/clover-onboarding-guide.pdf',
  toast:
    'https://vgrubs-prod.nyc3.cdn.digitaloceanspaces.com/guides/toast-onboarding-guide.pdf',
};

export const menuHref = (
  locationId: string | number,
  brandId: string | number
) => `/menu/${locationId}/location/${brandId}/brand`;

export const buildPosSections = (
  locationId: string | number
): PlaybookSection[] => [
  {
    id: 'sourceOfTruth',
    title: 'The POS is the source of truth',
    blocks: [
      {
        kind: 'text',
        text: 'On a POS location the menu is not maintained in vGrubs. It is pulled from the POS exactly as it stands there, so that the POS and every marketplace always agree.',
      },
      {
        kind: 'bullets',
        items: [
          'Items, prices, modifiers and availability all come from the POS. Nothing is added here by hand, and there are no custom items.',
          'Pausing an item, marking it sold out, renaming it, any menu management at all: do it in the POS, then import again.',
          'Clover, Toast and Lightspeed O are the POS systems live today. The rest of the list is switched on as clients ask for it.',
        ],
      },
    ],
  },
  {
    id: 'order',
    title: 'The order of work',
    blocks: [
      {
        kind: 'steps',
        items: [
          'Connect the physical location to the POS.',
          'Import the POS items into the location menu.',
          'Publish that menu to the marketplaces of the physical location.',
          'Do the same for every virtual restaurant: import from the same POS into the brand menu, then publish it.',
        ],
      },
      {
        kind: 'callout',
        tone: 'danger',
        text: 'The virtual restaurants belong to the same run, not to a later one. A brand menu has to map to the POS item for item, and only a fresh import and publish does that. Until a brand has been pushed again its orders are not accepted.',
      },
      {
        kind: 'bullets',
        items: [
          'A brand that is not POS enabled keeps taking its orders on the tablet in the meantime.',
          'The import only offers what the POS has. A brand menu gets no custom items either.',
        ],
      },
      {
        kind: 'links',
        links: [
          { kind: 'tab', tab: 'pos', label: 'Open the POS tab' },
          {
            kind: 'url',
            href: menuHref(locationId, 0),
            label: 'Open the location menu',
          },
          { kind: 'workingHours', label: 'Working hours' },
        ],
      },
    ],
  },
  {
    id: 'prices',
    title: 'A different price per marketplace',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'Toast and Lightspeed O allow it. An item price can be customised per marketplace on the item edit.',
          'Clover does not. The price has to be the same on every marketplace, or Clover rejects the order.',
        ],
      },
      {
        kind: 'text',
        text: 'The menu screen follows this on its own. Where the price edit is disabled on a POS menu, it is because the POS behind it does not allow a different price per marketplace.',
      },
    ],
  },
  {
    id: 'beforeConnect',
    title: 'Before you connect the POS',
    blocks: [
      {
        kind: 'bullets',
        items: [
          "The client's POS login. The owner, or a manager with admin rights, signs in and approves vGrubs.",
          'A marketplace still running through KitchenHub has to be moved to FoodHub first. The POS connection is refused with an error while one is left on KitchenHub.',
        ],
      },
      {
        kind: 'text',
        text: 'Clover and Toast each have a written walkthrough of the connection itself. Follow it rather than working from memory, the screens on their side change.',
      },
      {
        kind: 'links',
        links: [
          {
            kind: 'url',
            href: POS_GUIDE_PDFS.clover,
            label: 'Clover onboarding guide (PDF)',
          },
          {
            kind: 'url',
            href: POS_GUIDE_PDFS.toast,
            label: 'Toast onboarding guide (PDF)',
          },
        ],
      },
      {
        kind: 'text',
        text: 'Lightspeed O has no guide yet, no client has been onboarded onto it so far. Write the steps down on the first one so the next person is not starting from scratch.',
      },
    ],
  },
  {
    id: 'unsupportedPos',
    title: 'A client on a POS we do not support',
    blocks: [
      {
        kind: 'text',
        text: 'Not every client runs Clover, Toast or Lightspeed O. Where the POS is one vGrubs does not integrate with, connect the marketplaces through KitchenHub in scraping, view only mode: orders come in, and no menu is published from vGrubs.',
      },
    ],
  },
];

/**
 * Built on demand rather than held as a constant: the timing table calls
 * renderProviderName, and helpers is heavy enough that evaluating it while
 * this module initialises closes an import cycle and leaves the page blank.
 */
export const buildMarketplaceSections = (): PlaybookSection[] => [
  {
    id: 'foodhub',
    title: 'On a POS location everything goes through FoodHub',
    blocks: [
      {
        kind: 'text',
        text: 'Under Choose Ordering Service pick FoodHub, then connect. KitchenHub bypasses the POS, so orders placed through it would never reach the POS.',
      },
      {
        kind: 'callout',
        tone: 'info',
        text: 'A published menu and configured working hours are required for every provider without exception. The connection cannot be made before both are in place.',
      },
      {
        kind: 'image',
        src: playbookFoodhubConnect,
        alt: 'The Connect Provider dialog with FoodHub picked as the ordering service',
      },
      {
        kind: 'links',
        links: [
          {
            kind: 'tab',
            tab: 'marketplace',
            label: 'Open the Marketplace tab',
          },
          { kind: 'workingHours', label: 'Working hours' },
        ],
      },
    ],
  },
  {
    id: 'timing',
    title: 'How long a connection takes',
    blocks: [
      {
        kind: 'timings',
        // Built from the same map the provider rows read, so the table and
        // the rows can never disagree.
        rows: [
          ...TIMING_TABLE_PROVIDERS.map((provider) => {
            const timing = providerConnectionTiming(provider);
            return {
              // renderProviderName covers every provider listed here; the
              // fallback is only there to satisfy its wider return type.
              provider: renderProviderName(provider) ?? provider,
              timing: timing.label,
              note: timing.note,
            };
          }),
          {
            provider: 'Everything else',
            timing: DEFAULT_CONNECTION_TIMING.label,
          },
        ],
      },
    ],
  },
  {
    id: 'grubhub',
    title: 'Moving a Grubhub integration onto FoodHub',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'Send the store IDs to the FoodHub Operating team in the WhatsApp group, so they push the Grubhub team to make the move.',
          'This is the usual case when a location is coming off KitchenHub onto FoodHub.',
        ],
      },
    ],
  },
  {
    id: 'posNamed',
    title: 'Why there is a Grubhub POS and a Doordash POS',
    blocks: [
      {
        kind: 'text',
        text: 'KitchenHub scraped Grubhub and DoorDash orders before either had a direct integration. It kept the scraped ones under the plain names, Grubhub and Doordash, and called the direct ones Grubhub POS and Doordash POS. FoodHub calls its direct connection simply Grubhub.',
      },
      {
        kind: 'bullets',
        items: [
          'Grubhub POS and Doordash POS are KitchenHub only. FoodHub has no Grubhub POS.',
          'That is what the transfer is for: moving a Grubhub integration onto Grubhub POS frees the Grubhub row for the FoodHub connection.',
        ],
      },
      {
        kind: 'callout',
        tone: 'danger',
        text: 'They connect instantly, but do not connect one while a POS connection is still pending. It blocks the stable Grubhub connection, and Grubhub has refused the move when it saw one already connected.',
      },
    ],
  },
  {
    id: 'viewOnly',
    title: 'KitchenHub view only',
    blocks: [
      {
        kind: 'text',
        text: 'A KitchenHub Uber connection can be set to view only: orders come in without a menu being published from vGrubs. FoodHub has no equivalent, it is always a full direct connection.',
      },
      {
        kind: 'bullets',
        items: [
          'Not used for a POS integration.',
          'It is what to use for a client whose POS vGrubs does not support.',
        ],
      },
    ],
  },
  {
    id: 'noPosSupport',
    title: 'Marketplaces that block a POS integration',
    blocks: [
      {
        kind: 'text',
        text: 'ChowNow, EatStreet, Menufy and Nabe have no POS compatible connection. Every other marketplace does.',
      },
      {
        kind: 'callout',
        tone: 'danger',
        text: 'A location cannot go on a POS while it sells on any of these four. The client has to disconnect them first, and there is no workaround for now, so settle it with the client before the POS work starts.',
      },
    ],
  },
];
